import { ParsedQuery } from 'query-string';

import { HOME_ROUTE } from '../../web-routes';

import { allPossibleStatuses } from '../../config/application-status-config';
import { parseRequiredId } from '../../util/parse-required-id';

export type ApplicantDirectoryQueryParams = {
  accountId?: number;
  orgId?: number;

  filterByOrgId?: string;
  // consider renaming this param to searchTerm or something similar if used to match other applicant fields
  // currently named for explicitness for the two fields we allow searching on
  filterByNameOrPhoneNumber?: string;
  filterByJobId?: string;
  filterByLocation?: string;
  filterByStatus?: ApplicationStatus;
  filterByReason?: string;

  includeRejectedExpired?: 'true';
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';

  previousLocationPath?: string;
  previousLocation?: string;
};

const defaultPreviousLocationPath = HOME_ROUTE;
const defaultQueryParamValues = (
  accountId: number,
  orgId?: number,
): ApplicantDirectoryQueryParams => ({
  accountId,
  orgId,
  filterByOrgId: undefined,
  filterByNameOrPhoneNumber: undefined,
  filterByJobId: undefined,
  filterByLocation: undefined,
  filterByStatus: undefined,
  filterByReason: undefined,

  includeRejectedExpired: undefined,
  sortBy: 'name',
  sortDirection: 'asc',

  previousLocationPath: defaultPreviousLocationPath,
  previousLocation: 'Home',
});

export const isApplicationStatus = (s: string | string[]): s is ApplicationStatus =>
  !Array.isArray(s) && allPossibleStatuses.includes(s as ApplicationStatus);

const isValidFilterById = (id: string | string[]): id is string => !!parseRequiredId(id);

const isSortDirection = (direction: string | string[]): direction is 'asc' | 'desc' =>
  !Array.isArray(direction) && ['asc', 'desc'].includes(direction);

export const validateApplicantDirectoryQueryParams = (
  params: ParsedQuery<string>,
): ApplicantDirectoryQueryParams => {
  const parsedAccountId = parseRequiredId(params.accountId);
  if (!parsedAccountId) {
    // we shouldn't get here, this is more an issue for developers
    // the redirect should handle the case of no accountId before we try to validate params
    throw new Error('AccountId needed to view applicant directory');
  }
  const parsedOrgId = parseRequiredId(params.orgId) || undefined;
  const validatedParams = { ...defaultQueryParamValues(parsedAccountId, parsedOrgId) };
  if (params.filterByNameOrPhoneNumber && !Array.isArray(params.filterByNameOrPhoneNumber)) {
    validatedParams.filterByNameOrPhoneNumber = params.filterByNameOrPhoneNumber;
  }
  if (params.filterByJobId && isValidFilterById(params.filterByJobId)) {
    validatedParams.filterByJobId = params.filterByJobId;
  }
  if (params.filterByOrgId && isValidFilterById(params.filterByOrgId)) {
    validatedParams.filterByOrgId = params.filterByOrgId;
  }
  if (params.filterByLocation && !Array.isArray(params.filterByLocation)) {
    validatedParams.filterByLocation = params.filterByLocation;
  }
  if (params.filterByStatus && isApplicationStatus(params.filterByStatus)) {
    validatedParams.filterByStatus = params.filterByStatus;
  }
  if (params.filterByReason && !Array.isArray(params.filterByReason)) {
    validatedParams.filterByReason = params.filterByReason;
  }
  if (params.includeRejectedExpired && params.includeRejectedExpired === 'true') {
    validatedParams.includeRejectedExpired = params.includeRejectedExpired;
  }
  if (params.sortDirection && isSortDirection(params.sortDirection)) {
    validatedParams.sortDirection = params.sortDirection;
  }
  if (params.sortBy && !Array.isArray(params.sortBy)) {
    validatedParams.sortBy = params.sortBy;
  }
  if (params.previousLocationPath && !Array.isArray(params.previousLocationPath)) {
    validatedParams.previousLocationPath = params.previousLocationPath;
  }
  if (
    validatedParams.previousLocationPath !== defaultPreviousLocationPath &&
    (params.previousLocation === undefined || typeof params.previousLocation === 'string')
  ) {
    // we only want to use the default value here if used the default value for the path
    validatedParams.previousLocation = params.previousLocation;
  }

  return validatedParams;
};

export const groupedStatusChangeReasons = (
  statusChangeReasons: StatusChangeReason[],
  excludedStatuses?: ApplicationStatus[],
) => {
  const reasonsToUse = excludedStatuses
    ? statusChangeReasons.filter((scr) => !excludedStatuses.includes(scr.toStatus))
    : statusChangeReasons;
  return reasonsToUse.reduce((acc: { [reasons: string]: number[] }, scr) => {
    if (acc[scr.reasonDescription]) {
      return { ...acc, [scr.reasonDescription]: [...acc[scr.reasonDescription], scr.reasonId] };
    }
    return { ...acc, [scr.reasonDescription]: [scr.reasonId] };
  }, {});
};
